import { useEffect, useState } from 'react'
import { ArrowUpRight, ListTree, Plus } from 'lucide-react'
import { useTasks } from '../TaskProvider'
import { referenceTargets } from '../connectedBible'
import type { StoryProject } from '../types'
import { draftTask } from '../taskOrganization'
import { useRouteState } from '../routeState'
import { TaskOrganizer } from './TaskOrganizer'

export function WritingTaskControl({ project, targetId, onSaveStory }: {
  project: StoryProject
  targetId: string
  onSaveStory: () => Promise<boolean>
}) {
  const store = useTasks()
  const scope = store.writingScope
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const [parentChoice, setParentChoice] = useState<string | undefined>()
  const [parentRoute, setParentRoute] = useRouteState<string | null>('writing-task-parent', null)
  useEffect(() => { setParentChoice(undefined); setError('') }, [project.id, targetId, scope])
  const targets = referenceTargets(project)
  const title = targets.find(t => t.id === targetId)?.title || 'this entry'
  const record = store.tasks.find(t => t.reference && t.projectId === project.id &&
    t.targetId === targetId && t.scope === scope)
  const task = record?.kind === 'task' ? record : null
  const storyParentId = project.bible?.parents?.find(p => p.childId === targetId)?.parentId
  const chosenParentId = task ? task.parentId || '' : parentChoice ?? record?.parentId
  const parentName = chosenParentId !== undefined
    ? chosenParentId ? store.tasks.find(t => t.id === chosenParentId)?.title || 'Parent unavailable' : ''
    : targets.find(t => t.id === storyParentId)?.title || ''
  const act = async () => {
    setPending(true)
    setError('')
    try {
      if (!await onSaveStory()) throw new Error('Save this writing successfully before changing its task status.')
      if (task) await store.save({ status: task.archived || task.status === 'done' ? 'todo' : 'done', archived: false }, task)
      else await store.track(project.id, targetId, scope, parentChoice)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The writing task could not be saved.')
    } finally { setPending(false) }
  }
  return <div className="writing-task-controls" aria-label={'Writing task for ' + title}>
    <div className="writing-task-actions">
      {!store.loaded || store.error ? <button type="button" className="text-button" onClick={() => void store.refresh()} disabled={store.loading}>
        {store.loading ? 'Checking task…' : 'Refresh task status'}
      </button> : task && !task.archived ? <label className="writing-task-check">
        <input type="checkbox" checked={task.status === 'done'} disabled={pending || store.busy}
          aria-label={task.status === 'done' ? 'Reopen task for ' + title : 'Mark task complete for ' + title}
          onChange={() => void act()} />
        <span>{pending ? 'Saving…' : task.status === 'done' ? 'Completed' : 'Mark complete'}</span>
      </label> : <button type="button" className="text-button writing-task-add" disabled={pending || store.busy}
        onClick={() => void act()}><Plus size={14} />{pending ? 'Saving…' : task?.archived ? 'Reopen task' : 'Add to tasks'}</button>}
      <select aria-label={'Task visibility for ' + title} value={scope} disabled={pending || store.busy}
        onChange={e => { store.setWritingScope(e.target.value as typeof scope); setError('') }}>
        <option value="team">Team</option><option value="personal">Personal</option>
      </select>
      {record && <button type="button" className="icon-button" aria-label={'Open ' + title + ' in Tasks'}
        title="Open in Tasks" onClick={() => store.openPage({ taskId: record.id })}><ArrowUpRight size={15} /></button>}
    </div>
    <div className="writing-task-parent">
      <button type="button" className="button writing-parent-button" disabled={!store.loaded || pending || store.busy}
        aria-haspopup="dialog" aria-expanded={parentRoute === targetId} onClick={() => setParentRoute(targetId)}><ListTree size={14} />Choose parent</button>
      {parentName && <span className="writing-parent-name" aria-label={'Task parent for ' + title} aria-live="polite">Parent: <strong>{parentName}</strong></span>}
    </div>
    {error && <p role="alert" className="writing-task-error">{error}</p>}
    {parentRoute === targetId && <TaskOrganizer source={record || draftTask(title, scope, store.user.id)} onClose={() => setParentRoute(null)} onChoose={id => {
      setParentRoute(null)
      if (task) void store.save({ parentId: id }, task).catch(e => setError(e.message))
      else setParentChoice(id)
    }} />}
  </div>
}
